/* CONTEXT:START
Wave: 3
Status: implemented
Stub-Type: mock-data
Dependencies: []
Risk-Level: low
Validation-Method: unit-test
Performance-Target: <100ms
Last-Updated: 2025-08-03
CONTEXT:END */

import { 
  PenholderAssignment, 
  PenholderMetrics, 
  Submission, 
  WorkflowState 
} from '../types';

/**
 * Mock service for penholder operations during Wave 1 implementation
 * Provides realistic sample data matching v1.1 backend interfaces
 */
export class MockPenholderService {
  
  // Sample penholder assignment data
  private mockAssignments: PenholderAssignment[] = [
    {
      id: 'assignment-001',
      penholderUserId: 'user-penholder-123',
      month: 8,
      year: 2025,
      assignedAt: '2025-08-01T00:00:00Z',
      isActive: true
    },
    {
      id: 'assignment-002',
      penholderUserId: 'user-penholder-456',
      month: 7,
      year: 2025,
      assignedAt: '2025-07-01T00:00:00Z',
      isActive: false
    }
  ];

  // Sample submissions with v1.1 penholder fields
  private mockSubmissions: Submission[] = [
    {
      // Existing v1.0 fields
      id: 'sub-001',
      userId: 'user-456',
      type: 'highlight',
      content: 'Successfully implemented new authentication system with 99.9% uptime',
      year: 2025,
      month: 8,
      createdAt: new Date('2025-08-01T10:00:00Z'),
      updatedAt: new Date('2025-08-02T14:00:00Z'),
      
      // New v1.1 fields
      currentVersion: '1.1',
      originalVersion: '1.0',
      workflowState: 'under_review',
      addedToReport: false,
      isIncludedInReport: false,
      penholderAssignedTo: 'user-penholder-123',
      penholderAssignedAt: '2025-08-01T12:00:00Z',
      penholderReviewDeadline: '2025-08-08T12:00:00Z',
      penholderFeedback: 'Please add more technical details about the implementation approach',
      penholderEditedAt: '2025-08-02T14:00:00Z'
    },
    {
      id: 'sub-002',
      userId: 'user-789',
      type: 'lowlight',
      content: 'Database migration took longer than expected due to data validation issues',
      year: 2025,
      month: 8,
      createdAt: new Date('2025-08-02T09:00:00Z'),
      updatedAt: new Date('2025-08-02T09:00:00Z'),
      
      currentVersion: '1.0',
      originalVersion: '1.0',
      workflowState: 'submitted',
      addedToReport: false,
      isIncludedInReport: false,
      penholderAssignedTo: 'user-penholder-123',
      penholderAssignedAt: '2025-08-02T10:00:00Z',
      penholderReviewDeadline: '2025-08-09T10:00:00Z'
    },
    {
      id: 'sub-003',
      userId: 'user-321',
      type: 'highlight',
      content: 'Reduced API response time by 40% through caching optimization',
      year: 2025,
      month: 8,
      createdAt: new Date('2025-08-01T15:00:00Z'),
      updatedAt: new Date('2025-08-03T11:00:00Z'),
      
      currentVersion: '1.2',
      originalVersion: '1.0',
      workflowState: 'added_to_report',
      addedToReport: true,
      isIncludedInReport: false,
      penholderAssignedTo: 'user-penholder-123',
      penholderAssignedAt: '2025-08-01T16:00:00Z',
      penholderReviewDeadline: '2025-08-08T16:00:00Z',
      penholderFeedback: 'Excellent detail on performance improvements. Approved for inclusion.',
      penholderEditedAt: '2025-08-03T11:00:00Z'
    }
  ];

  /* CONTEXT:START
  Wave: 1
  Status: stub
  Feature: Submissions Metrics - Updated mock service for new metrics structure
  Dependencies: [Updated PenholderMetrics interface]
  Risk-Level: low
  Performance-Target: <100ms
  Last-Updated: 2025-08-06
  CONTEXT:END */

  // Sample metrics data - Wave 1 structure
  private mockMetrics: PenholderMetrics = {
    penholderUserId: 'user-penholder-123',
    year: 2025,
    month: 8,
    submitted: 45,
    addedToReport: 12,
    pendingReview: 8,
    notChosen: 25,
    // Backward compatibility fields
    totalAssigned: 45,
    totalReviewed: 37,
    totalEdited: 5,
    averageReviewTime: 24.5 // hours
  };

  /**
   * Get current active penholder assignment
   */
  async getCurrentAssignment(): Promise<PenholderAssignment | null> {
    // Simulate API delay
    await this.delay(150);
    
    return this.mockAssignments.find(assignment => assignment.isActive) || null;
  }

  /**
   * Get submissions assigned to current penholder
   */
  async getAssignedSubmissions(penholderUserId?: string): Promise<Submission[]> {
    await this.delay(200);
    
    if (!penholderUserId) {
      const currentAssignment = await this.getCurrentAssignment();
      if (!currentAssignment) return [];
      penholderUserId = currentAssignment.penholderUserId;
    }
    
    return this.mockSubmissions.filter(
      submission => submission.penholderAssignedTo === penholderUserId
    );
  }

  /**
   * Get penholder performance metrics
   */
  async getMetrics(penholderUserId: string, year: number, month: number): Promise<PenholderMetrics> {
    await this.delay(100);
    
    // Return mock metrics (in real implementation, would filter by parameters)
    return this.mockMetrics;
  }

  /**
   * Assign penholder for a specific month/year
   */
  async assignPenholder(userId: string, month: number, year: number): Promise<PenholderAssignment> {
    await this.delay(300);
    
    // Deactivate current assignment
    this.mockAssignments.forEach(assignment => {
      if (assignment.month === month && assignment.year === year) {
        assignment.isActive = false;
      }
    });
    
    // Create new assignment
    const newAssignment: PenholderAssignment = {
      id: `assignment-${Date.now()}`,
      penholderUserId: userId,
      month,
      year,
      assignedAt: new Date().toISOString(),
      isActive: true
    };
    
    this.mockAssignments.push(newAssignment);
    return newAssignment;
  }

  /**
   * Update submission workflow state
   */
  async updateSubmissionState(
    submissionId: string, 
    newState: WorkflowState, 
    feedback?: string
  ): Promise<Submission> {
    await this.delay(250);
    
    const submission = this.mockSubmissions.find(s => s.id === submissionId);
    if (!submission) {
      throw new Error(`Submission ${submissionId} not found`);
    }
    
    // Update submission state
    submission.workflowState = newState;
    submission.updatedAt = new Date();
    
    if (feedback) {
      submission.penholderFeedback = feedback;
      submission.penholderEditedAt = new Date().toISOString();
    }
    
    // Update approval status based on state
    if (newState === 'added_to_report') {
      submission.addedToReport = true;
      submission.addedToReportAt = new Date().toISOString();
      submission.addedToReportBy = 'user-penholder-123';
    } else if (newState === 'not_added_to_report') {
      submission.addedToReport = false;
      submission.reportExclusionReason = 'Does not meet report criteria';
    }
    
    return submission;
  }

  /**
   * Get all penholder assignments (for admin)
   */
  async getAllAssignments(): Promise<PenholderAssignment[]> {
    await this.delay(180);
    return [...this.mockAssignments];
  }

  /**
   * Get assignment history for metrics
   */
  async getAssignmentHistory(year: number): Promise<PenholderAssignment[]> {
    await this.delay(150);
    return this.mockAssignments.filter(assignment => assignment.year === year);
  }

  /**
   * Simulate API delay for realistic UX
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export singleton instance
export const mockPenholderService = new MockPenholderService();
